import { useTable, useSelect } from "@refinedev/antd";
import { Card, Table, Space, Input, Select, Button, Tag, Tooltip, App, Typography } from "antd";
import {
  SearchOutlined,
  EditOutlined,
  StopOutlined,
  CheckCircleOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { PageHeader } from "../../components/common/PageHeader";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router";
import { useCustomMutation, useInvalidate, useList, useGetIdentity } from "@refinedev/core";
import { ADMIN_USER_TYPES, canWriteData, isEurosemillasScoped, type UserInfo } from "../../types/auth";
import dayjs from "dayjs";

/** ID of the "España" portfolio — used as default filter */
const SPAIN_PORTFOLIO_ID = 1;

interface VarietyBreakdown {
  id: number | null;
  code: string;
  name: string;
  // PR4 — Σ Plot.surface por variedad (declarada en contrato).
  licensed_surface: number;
  // PR4 — Σ PlotReal.surface por variedad (auditada).
  audited_surface: number;
  // [legacy] alias de licensed_surface, mantenido por compat.
  surface?: number;
}

interface FarmListItem {
  id: number;
  name: string;
  producer: number;
  producer_name: string;
  province_name: string;
  town_name: string;
  // [DEPRECATED] PR4: `surface_tango` (manual) ya no se muestra; la
  // UI lee `licensed_surface_total` derivado de las Plot.
  surface_tango: number;
  audited_surface: number;
  surface_diff: number;
  last_audit_date: string | null;
  // Anotaciones para el cell rico de 'Última auditoría':
  //   audit_count           — total auditorías no canceladas
  //   pending_audit_count   — sin start_date, no canceladas
  //   current_season_audit_id — id de audit en campaña actual (null si no hay)
  audit_count?: number;
  pending_audit_count?: number;
  current_season_audit_id?: number | null;
  // PR5 — Estado de validación de superficie (admin-only).
  surface_review_status?: string | null;
  is_active: boolean;
  // PR3 — Desglose por variedad agregado en el backend.
  varieties?: VarietyBreakdown[];
  // PR4 — Totales derivados del desglose.
  licensed_surface_total?: number;
  audited_surface_total?: number;
  surface_diff_total?: number;
}

/**
 * FarmList Component
 * Server-side paginated list of farms with portfolio filter and search.
 */
export const FarmList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: identity } = useGetIdentity<UserInfo>();
  const isAdmin = (ADMIN_USER_TYPES as readonly number[]).includes(
    identity?.app_user?.user_type ?? -1,
  );
  const canWrite = canWriteData(identity);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [portfolioId, setPortfolioId] = useState<number | undefined>(
    searchParams.get("portfolio") ? Number(searchParams.get("portfolio")) : SPAIN_PORTFOLIO_ID,
  );
  // ESA users no tienen España (1) en su scope. Al cargar `identity`,
  // si el URL no fuerza otro portfolio, retargeteamos al portfolio
  // asignado al ESA para que el Select muestre una opción válida.
  useEffect(() => {
    if (searchParams.get("portfolio")) return;
    if (!identity) return;
    if (!isEurosemillasScoped(identity)) return;
    const p = identity.app_user?.portfolio;
    if (p && p !== portfolioId) setPortfolioId(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity]);
  // searchText y statusFilter persisten en la URL para que el
  // back-from-detail con `navigate(returnPath)` los restaure sin
  // plumbing extra.
  const [searchText, setSearchText] = useState<string>(
    searchParams.get("q") ?? "",
  );
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    searchParams.get("is_active") ?? "true",
  );
  // PR3 — Filtro multi por variedad protegida (`?variety=1&variety=7`).
  // El backend lo recibe vía `BaseInFilter` y agrega `plot.variety_id`.
  const [varietyFilter, setVarietyFilter] = useState<number[]>(() => {
    const raw = searchParams.getAll("variety");
    return raw.map((v) => Number(v)).filter((n) => Number.isFinite(n));
  });
  // Nuevo filtro para la columna 'Última auditoría': uno de
  // never | current_season | over_2y | over_3y | over_5y, o vacío.
  const [lastAuditFilter, setLastAuditFilter] = useState<string | undefined>(
    searchParams.get("last_audit_filter") ?? undefined,
  );
  // PR5 — Filtro de revisión de superficie (admin-only). Mismos
  // buckets que /audits: pending | irregularity | reviewed | ok.
  const [surfaceReviewFilter, setSurfaceReviewFilter] = useState<
    string | undefined
  >(searchParams.get("surface_review_filter") ?? undefined);

  const permanentFilters: any[] = [];
  // Buscador de texto: metemos `q` en permanentFilters (además de en
  // el `setFilters` de handleSearch para el UX de tecleo) para que al
  // remontarse el componente desde la URL — típicamente al volver del
  // detalle — el filtro se aplique sin necesidad de re-teclear.
  const trimmedSearch = searchText.trim();
  if (trimmedSearch) {
    permanentFilters.push({ field: "q", operator: "eq", value: trimmedSearch });
  }
  if (portfolioId) permanentFilters.push({ field: "portfolio", operator: "eq", value: portfolioId });
  if (statusFilter !== undefined) permanentFilters.push({ field: "is_active", operator: "eq", value: statusFilter });
  // PR4 fix — el dataProvider serializa el value tal cual; axios
  // entonces hace `?variety[]=1&variety[]=7` para arrays, pero
  // `BaseInFilter` del backend espera la forma CSV `?variety=1,7`.
  // Pasamos el CSV directamente para que ambos lados se entiendan.
  if (varietyFilter.length > 0) {
    permanentFilters.push({
      field: "variety",
      operator: "eq",
      value: varietyFilter.join(","),
    });
  }
  if (lastAuditFilter) {
    permanentFilters.push({
      field: "last_audit_filter",
      operator: "eq",
      value: lastAuditFilter,
    });
  }
  if (surfaceReviewFilter) {
    permanentFilters.push({
      field: "surface_review_filter",
      operator: "eq",
      value: surfaceReviewFilter,
    });
  }

  const { tableProps, setFilters } = useTable<FarmListItem>({
    resource: "farms",
    pagination: {
      mode: "server",
      pageSize: 25,
    },
    filters: {
      permanent: permanentFilters,
    },
    sorters: {
      mode: "server",
      initial: [{ field: "name", order: "asc" }],
    },
    syncWithLocation: false,
  });

  const { selectProps: portfolioSelectProps } = useSelect({
    resource: "portfolios",
    optionLabel: "name" as any,
    optionValue: "id" as any,
  });

  // PR3 — Catálogo de variedades para el filtro multi de la tabla.
  const { result: varietyResult } = useList<{ id: number; name: string; code: string }>({
    resource: "varieties",
    pagination: { mode: "off" },
    queryOptions: { staleTime: 5 * 60 * 1000 },
  });
  const varietyOptions = (varietyResult?.data ?? []).map((v: any) => ({
    value: v.id,
    label: v.name,
  }));

  const { mutate: toggleActive } = useCustomMutation();
  const invalidate = useInvalidate();
  const { message } = App.useApp();

  const handleToggleActive = (farmId: number, currentlyActive: boolean) => {
    toggleActive(
      {
        url: `/v1/farms/${farmId}/`,
        method: "patch",
        values: { is_active: !currentlyActive },
      },
      {
        onSuccess: () => {
          message.success(currentlyActive ? t("farms.messages.deactivated", "Finca desactivada") : t("farms.messages.activated", "Finca activada"));
          invalidate({ resource: "farms", invalidates: ["list"] });
        },
      },
    );
  };

  const handleSearch = (value: string) => {
    const trimmed = value.trim();
    setSearchText(value);
    if (trimmed) {
      setFilters([{ field: "q", operator: "eq", value: trimmed }], "merge");
    } else {
      setFilters([{ field: "q", operator: "eq", value: undefined }], "merge");
    }
    const params = new URLSearchParams(searchParams);
    if (trimmed) params.set("q", trimmed);
    else params.delete("q");
    setSearchParams(params, { replace: true });
  };

  // Si volvemos del detalle con un scrollY capturado, restauramos
  // tras un tick para que la tabla haya pintado su altura. El state
  // se consume y se borra para que un F5 no re-scrolee.
  useEffect(() => {
    const restoreScroll = (location.state as any)?.restoreScroll;
    if (typeof restoreScroll === "number") {
      const t = setTimeout(() => {
        window.scrollTo({ top: restoreScroll, behavior: "auto" });
      }, 50);
      // Limpiar el state para evitar re-scrolls espurios.
      window.history.replaceState({}, "");
      return () => clearTimeout(t);
    }
  }, [location.state]);

  return (
    <Card>
      <PageHeader
        icon={<EnvironmentOutlined />}
        accent="#13C2C2"
        title={t("farms.title", "Gestión de Fincas")}
        subtitle={t(
          "farms.subtitle",
          "Listado de fincas y su superficie auditada",
        )}
      />
      <Space size="middle" style={{ marginBottom: 16 }} wrap>
        <Input.Search
          placeholder={t("farms.search_placeholder", "Buscar por nombre de finca o productor...")}
          allowClear
          enterButton={<SearchOutlined />}
          size="middle"
          style={{ width: 400 }}
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            if (!e.target.value.trim()) handleSearch("");
          }}
          onSearch={handleSearch}
        />
        <Select<number>
          options={portfolioSelectProps.options}
          loading={portfolioSelectProps.loading}
          placeholder={t("farms.fields.portfolio", "Cartera")}
          allowClear
          style={{ width: 220 }}
          value={portfolioId}
          onChange={(value) => {
            setPortfolioId(value);
            const params = new URLSearchParams(searchParams);
            if (value) {
              params.set("portfolio", String(value));
            } else {
              params.delete("portfolio");
            }
            setSearchParams(params, { replace: true });
          }}
        />
        <Select<string>
          options={[
            { value: "true", label: t("farms.status.active", "Activa") },
            { value: "false", label: t("farms.status.inactive", "Inactiva") },
          ]}
          placeholder={t("farms.fields.status", "Estado")}
          allowClear
          style={{ width: 160 }}
          value={statusFilter}
          onChange={(value) => {
            setStatusFilter(value);
            const params = new URLSearchParams(searchParams);
            if (value !== undefined) params.set("is_active", value);
            else params.delete("is_active");
            setSearchParams(params, { replace: true });
          }}
        />
        {/* El filtro de Variedad protegida se ha movido al header
            de la columna correspondiente — antd dropdown con search +
            multiselección. Aquí solo dejamos los filtros generales. */}

        {/* PR5 — Filtro de revisión de superficie (admin-only). */}
        {isAdmin && (
          <Select
            placeholder="Revisión superficie"
            style={{ width: 220 }}
            allowClear
            value={surfaceReviewFilter}
            onChange={(value) => {
              setSurfaceReviewFilter(value);
              const params = new URLSearchParams(searchParams);
              if (value) params.set("surface_review_filter", value);
              else params.delete("surface_review_filter");
              setSearchParams(params, { replace: true });
            }}
            options={[
              { value: "pending", label: "Pendiente revisión" },
              { value: "irregularity", label: "Irregularidad" },
              { value: "reviewed", label: "Ya revisadas" },
              { value: "ok", label: "OK (Δ = 0)" },
            ]}
          />
        )}
      </Space>

      {/* Contador de resultados + chips de filtros activos con × para
          quitarlos. El × es la forma fiable de reiniciar un filtro
          (el botón "Reset" del dropdown a veces se queda pegado). */}
      {(() => {
        const total =
          typeof tableProps.pagination === "object" && tableProps.pagination
            ? (tableProps.pagination as any).total
            : undefined;
        const lastAuditLabels: Record<string, string> = {
          never: "Sin auditar",
          current_season: "En campaña actual",
          over_2y: "Hace > 2 años",
          over_3y: "Hace > 3 años",
          over_5y: "Hace > 5 años",
        };
        const clearLastAudit = () => {
          setLastAuditFilter(undefined);
          const params = new URLSearchParams(searchParams);
          params.delete("last_audit_filter");
          setSearchParams(params, { replace: true });
        };
        const clearVariety = () => {
          setVarietyFilter([]);
          const params = new URLSearchParams(searchParams);
          params.delete("variety");
          setSearchParams(params, { replace: true });
        };
        if (total == null && !lastAuditFilter && varietyFilter.length === 0)
          return null;
        return (
          <div
            style={{
              marginBottom: 8,
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              alignItems: "center",
            }}
          >
            {total != null && (
              <>
                <Tag color="blue">
                  {total} {total === 1 ? "finca" : "fincas"}
                </Tag>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  tras filtros
                </Typography.Text>
              </>
            )}
            {lastAuditFilter && (
              <Tag
                closable
                onClose={clearLastAudit}
                color="orange"
                style={{ marginLeft: 8 }}
              >
                Última auditoría: {lastAuditLabels[lastAuditFilter] ?? lastAuditFilter}
              </Tag>
            )}
            {varietyFilter.length > 0 && (
              <Tag closable onClose={clearVariety} color="geekblue">
                Variedad: {varietyFilter.length}{" "}
                {varietyFilter.length === 1 ? "seleccionada" : "seleccionadas"}
              </Tag>
            )}
          </div>
        );
      })()}

      <Table
        {...tableProps}
        rowKey="id"
        className="planning-audits-table"
        size="small"
        scroll={{ x: 1200 }}
        // Click en cualquier parte de la fila → detalle de la finca.
        // Filtramos clicks que vienen de un botón de acción (editar,
        // activar/desactivar) para que no naveguen además de ejecutar
        // su acción. `closest("button")` cubre también los iconos
        // dentro del botón.
        onRow={(record) => ({
          onClick: (event) => {
            const target = event.target as HTMLElement;
            if (target.closest("button") || target.closest("a")) return;
            // Snapshot del listado para que el botón Volver del detalle
            // restaure exactamente esta URL (con filtros) y el scroll.
            navigate(`/farms/show/${record.id}`, {
              state: {
                from: "farms",
                listSnapshot: {
                  search: location.search || "",
                  scrollY: window.scrollY,
                },
              },
            });
          },
          style: { cursor: "pointer" },
        })}
        onChange={(pagination, filters, sorter, extra) => {
          // 1) Filtro de columna 'Última auditoría' (single-select).
          //    'all' es el sentinel para limpiar el filtro.
          const rawLast = (filters as any)?.last_audit_date;
          const pickedLast =
            Array.isArray(rawLast) && rawLast.length > 0
              ? String(rawLast[0])
              : undefined;
          const nextLast: string | undefined =
            pickedLast && pickedLast !== "all" ? pickedLast : undefined;
          if (nextLast !== lastAuditFilter) {
            setLastAuditFilter(nextLast);
            const params = new URLSearchParams(searchParams);
            if (nextLast) params.set("last_audit_filter", nextLast);
            else params.delete("last_audit_filter");
            setSearchParams(params, { replace: true });
          }
          // 2) Filtro de columna 'Variedad protegida' (multi-select).
          //    El key de la columna es 'variety'.
          const rawVar = (filters as any)?.variety;
          const nextVar: number[] = Array.isArray(rawVar)
            ? (rawVar
                .map((x: any) => Number(x))
                .filter((n: number) => Number.isFinite(n)) as number[])
            : [];
          const sameVar =
            nextVar.length === varietyFilter.length &&
            nextVar.every((v, i) => v === varietyFilter[i]);
          if (!sameVar) {
            setVarietyFilter(nextVar);
            const params = new URLSearchParams(searchParams);
            params.delete("variety");
            nextVar.forEach((v) => params.append("variety", String(v)));
            setSearchParams(params, { replace: true });
          }
          // Importante: reenviar al onChange que Refine inyecta vía
          // tableProps. Sin esto, paginación y ordenación dejan de
          // funcionar.
          (tableProps as any)?.onChange?.(pagination, filters, sorter, extra);
        }}
      >
        <Table.Column
          dataIndex="producer_name"
          title={t("farms.fields.producer", "Productor")}
          sorter
          key="producer__name"
          width={200}
        />
        <Table.Column
          dataIndex="name"
          title={t("farms.fields.name", "Finca")}
          sorter
          width={200}
        />
        <Table.Column
          dataIndex="town_name"
          title={t("farms.fields.town", "Municipio")}
          sorter
          key="town__name"
          width={160}
        />
        <Table.Column
          dataIndex="province_name"
          title={t("farms.fields.province", "Provincia")}
          sorter
          key="province__name"
          width={140}
        />
        <Table.Column
          key="variety"
          title={t("farms.fields.variety", "Variedad protegida")}
          width={220}
          // Filtro dropdown server-side: multi-select con búsqueda.
          // Los valores son IDs de Variety, se persisten en la URL como
          // ?variety=... y se pasan al backend vía permanentFilters.
          filteredValue={varietyFilter.length > 0 ? varietyFilter : null}
          filterSearch
          filterMultiple
          filters={varietyOptions.map((v) => ({
            text: v.label,
            value: v.value,
          }))}
          onFilter={() => true /* server-side */}
          // Sortable por la concatenación de nombres de variedades de
          // la finca. Para fincas multi-variedad ordena alfabéticamente
          // por la primera variedad alfabética; suficiente para que el
          // listado quede agrupado.
          sorter={(a: FarmListItem, b: FarmListItem) => {
            const labelA = (a.varieties ?? []).map((v) => v.name).join(", ");
            const labelB = (b.varieties ?? []).map((v) => v.name).join(", ");
            return labelA.localeCompare(labelB);
          }}
          render={(_: unknown, record: FarmListItem) => {
            const breakdown = record.varieties ?? [];
            if (breakdown.length === 0) {
              return <span style={{ color: "#bfbfbf" }}>—</span>;
            }
            return (
              <Space size={4} wrap>
                {breakdown.map((v) => {
                  // El backend devuelve los campos como Number en JSON
                  // pero los modelos GIS de Django a veces caen como
                  // string; normalizamos en cliente para que la suma
                  // y el `.toFixed` siempre funcionen.
                  const licensed = Number(v.licensed_surface ?? v.surface ?? 0) || 0;
                  const audited = Number(v.audited_surface ?? 0) || 0;
                  const diff = audited - licensed;
                  const sign = diff === 0 ? "" : diff > 0 ? "+" : "";
                  return (
                    <Tooltip
                      key={v.code}
                      title={
                        <div>
                          <div><strong>{v.name}</strong></div>
                          <div>Licenciada: {licensed.toFixed(2)} ha</div>
                          <div>Auditada: {audited.toFixed(2)} ha</div>
                          <div>
                            Diferencia: {sign}{diff.toFixed(2)} ha
                          </div>
                        </div>
                      }
                    >
                      <Tag color="blue" style={{ margin: 0 }}>
                        {v.name}
                      </Tag>
                    </Tooltip>
                  );
                })}
              </Space>
            );
          }}
        />
        {/* Sup. licenciada — Σ Plot.surface por finca. Tooltip con el
            desglose por variedad cuando hay más de una. */}
        <Table.Column
          dataIndex="licensed_surface_total"
          title={t("farms.fields.surface_licensed", "Sup. licenciada (ha)")}
          sorter={(a: FarmListItem, b: FarmListItem) =>
            (Number(a.licensed_surface_total) || 0) -
            (Number(b.licensed_surface_total) || 0)
          }
          width={130}
          align="right"
          render={(_: unknown, record: FarmListItem) => {
            const licensed = Number(record.licensed_surface_total) || 0;
            const breakdown = record.varieties ?? [];
            if (licensed === 0)
              return <span style={{ color: "#bfbfbf" }}>—</span>;
            const cell = (
              <span style={{ fontWeight: 500 }}>{licensed.toFixed(2)}</span>
            );
            if (breakdown.length <= 1) return cell;
            return (
              <Tooltip
                title={
                  <div>
                    {breakdown.map((v) => {
                      const vl = Number(v.licensed_surface ?? v.surface ?? 0) || 0;
                      return (
                        <div key={v.code} style={{ fontSize: 11 }}>
                          {v.name}: {vl.toFixed(2)} ha
                        </div>
                      );
                    })}
                    <div style={{ borderTop: "1px solid #555", marginTop: 4, paddingTop: 4 }}>
                      <strong>Total: {licensed.toFixed(2)} ha</strong>
                    </div>
                  </div>
                }
              >
                {cell}
              </Tooltip>
            );
          }}
        />
        {/* Sup. auditada + diferencia ha + % — todo apilado y
            sortable por auditada. */}
        <Table.Column
          dataIndex="audited_surface_total"
          title={t("farms.fields.surface_audited", "Sup. auditada (ha)")}
          sorter={(a: FarmListItem, b: FarmListItem) =>
            (Number(a.audited_surface_total) || 0) -
            (Number(b.audited_surface_total) || 0)
          }
          width={160}
          align="right"
          render={(_: unknown, record: FarmListItem) => {
            const licensed = Number(record.licensed_surface_total) || 0;
            const audited = Number(record.audited_surface_total) || 0;
            const diff = audited - licensed;
            const breakdown = record.varieties ?? [];
            if (licensed === 0 && audited === 0)
              return <span style={{ color: "#bfbfbf" }}>—</span>;
            const absDiff = Math.abs(diff);
            const displayDiff = Math.round(absDiff * 100) / 100;
            const pct = licensed > 0 ? (absDiff / licensed) * 100 : 0;
            const color =
              displayDiff === 0
                ? "#8c8c8c"
                : diff > 0
                  ? "#f5222d"
                  : "#52c41a";
            const sign = displayDiff === 0 ? "" : diff > 0 ? "+" : "";
            const cell = (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  lineHeight: 1.2,
                }}
              >
                <span style={{ fontWeight: 600 }}>{audited.toFixed(2)}</span>
                {(licensed > 0 || audited > 0) && (
                  <span style={{ fontSize: 10, color, fontWeight: 500 }}>
                    {sign}
                    {diff.toFixed(2)}
                    {licensed > 0 ? ` · ${sign}${pct.toFixed(0)}%` : ""}
                  </span>
                )}
              </div>
            );
            if (breakdown.length <= 1) return cell;
            return (
              <Tooltip
                title={
                  <div>
                    {breakdown.map((v) => {
                      const va = Number(v.audited_surface ?? 0) || 0;
                      const vl = Number(v.licensed_surface ?? v.surface ?? 0) || 0;
                      return (
                        <div key={v.code} style={{ fontSize: 11 }}>
                          {v.name}: {va.toFixed(2)} / {vl.toFixed(2)} ha
                        </div>
                      );
                    })}
                    <div style={{ borderTop: "1px solid #555", marginTop: 4, paddingTop: 4 }}>
                      <strong>Auditada total: {audited.toFixed(2)} ha</strong>
                    </div>
                    <div>Δ vs licenciada: {sign}{diff.toFixed(2)} ha ({sign}{pct.toFixed(1)}%)</div>
                  </div>
                }
              >
                {cell}
              </Tooltip>
            );
          }}
        />
        {/* PR5 — Estado de validación de superficie (admin-only). */}
        {isAdmin && (
          <Table.Column
            dataIndex="surface_review_status"
            title="Revisión sup."
            width={170}
            render={(value: string | null) => {
              if (!value) return <span style={{ color: "#bfbfbf" }}>—</span>;
              const meta: Record<
                string,
                { color: string; label: string }
              > = {
                ok: { color: "green", label: "OK" },
                reviewed: { color: "green", label: "Aprobada" },
                pending_review: { color: "gold", label: "Pendiente" },
                outdated: { color: "orange", label: "Re-validar" },
                irregularity: { color: "red", label: "Irregularidad" },
                no_data: { color: "default", label: "Sin snapshot" },
              };
              const m = meta[value] ?? { color: "default", label: value };
              return <Tag color={m.color}>{m.label}</Tag>;
            }}
          />
        )}
        <Table.Column
          dataIndex="last_audit_date"
          title={t("farms.fields.last_audit_date", "Última Auditoría")}
          sorter
          key="last_audit_date"
          width={170}
          align="center"
          // Filtro dropdown server-side: setea last_audit_filter en la
          // URL y en permanentFilters; el backend hace el resto. Usamos
          // filterMultiple=false porque las opciones son excluyentes.
          filterMultiple={false}
          filteredValue={lastAuditFilter ? [lastAuditFilter] : null}
          onFilter={() => true /* server-side */}
          // 'all' es el sentinel para "mostrar todas" — el handler lo
          // detecta y limpia el filtro server-side y la URL.
          filters={[
            { text: "Mostrar todas", value: "all" },
            { text: "Sin auditar", value: "never" },
            { text: "En campaña actual", value: "current_season" },
            { text: "Hace > 2 años", value: "over_2y" },
            { text: "Hace > 3 años", value: "over_3y" },
            { text: "Hace > 5 años", value: "over_5y" },
          ]}
          // antd llama onFilter por defecto pero al cambiar la
          // selección emite por onChange de la Table. Captamos vía
          // `filteredValue` controlado + el handler global de onChange
          // de la <Table>. Aquí solo configuramos el dropdown.
          render={(value: string | null, record: FarmListItem) => {
            // Sin auditorías totales (ni canceladas).
            if ((record.audit_count ?? 0) === 0) {
              return <Tag color="default">Sin auditar</Tag>;
            }
            // En campaña actual: cubre tanto audits en Season.year
            // actual como audits pendientes (sin start_date). Ambas
            // condiciones implican que la finca está en el flujo
            // activo de planificación.
            // El backend ya unifica los dos casos en
            // current_season_audit_id.
            if (record.current_season_audit_id) {
              return (
                <Space direction="vertical" size={0} align="center">
                  <Tag color="blue">Campaña actual</Tag>
                  {value ? (
                    <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                      Última: {dayjs(value).format("DD/MM/YYYY")}
                    </Typography.Text>
                  ) : (
                    <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                      sin fecha aún
                    </Typography.Text>
                  )}
                </Space>
              );
            }
            // Hay realizadas pero no hay audit en campaña actual:
            // fecha + color según antigüedad.
            if (value) {
              const ageY = dayjs().diff(dayjs(value), "year", true);
              const color =
                ageY > 3 ? "red" : ageY > 2 ? "orange" : ageY > 1 ? "gold" : "green";
              return <Tag color={color}>{dayjs(value).format("DD/MM/YYYY")}</Tag>;
            }
            return "—";
          }}
        />
        <Table.Column
          dataIndex="is_active"
          title={t("farms.fields.status", "Estado")}
          width={100}
          align="center"
          render={(value: boolean) =>
            value === false
              ? <Tag color="red">{t("farms.status.inactive", "Inactiva")}</Tag>
              : <Tag color="green">{t("farms.status.active", "Activa")}</Tag>
          }
        />
        <Table.Column
          title={t("common.actions", "Acciones")}
          width={120}
          align="center"
          render={(_: unknown, record: FarmListItem) => (
            <Space size="small">
              {/* "Ver" eliminado: pulsar la fila ya lleva al detalle. */}
              {canWrite && (
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/producers/${record.producer}/farms/edit/${record.id}`, { state: { from: "farms" } })}
                  title={t("common.edit", "Editar")}
                />
              )}
              {canWrite && (record.is_active !== false ? (
                <Button
                  type="text"
                  size="small"
                  icon={<StopOutlined />}
                  onClick={() => handleToggleActive(record.id, true)}
                  title={t("farms.actions.deactivate", "Desactivar")}
                />
              ) : (
                <Button
                  type="text"
                  size="small"
                  style={{ color: "#52c41a" }}
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleToggleActive(record.id, false)}
                  title={t("farms.actions.activate", "Activar")}
                />
              ))}
            </Space>
          )}
        />
      </Table>
    </Card>
  );
};
